import { useState } from "react";

import ErrorMessage from "common/components/ui/ErrorMessage";

import useLinkedInPost from "../../../datahooks/useLinkedInPost";
import useSelectedMinerAccount from "../../../hooks/useSelectedMinerAccount";
import LinkedInAccountFormField from "../LinkedInAccountFormField";
import ScrollAndFooter from "../ScrollAndFooter";
import PostFormField from "./PostFormField";
import SelectedPost from "./SelectedPost";

import type { PostLeadListSource } from "../../../types";

import PostImage from "assets/images/sources/post.png";

interface PostProps {
  createSource: (params: {
    miner: string;
    settings: PostLeadListSource["settings"];
  }) => void;
  isCreatingSource: boolean;
}

export default function Post({ createSource, isCreatingSource }: PostProps) {
  const { setMinerAccountId, minerAccountId } = useSelectedMinerAccount();

  const [postUrl, setPostUrl] = useState<string>();
  const [targetingParameters, setTargetingParameters] = useState<
    Omit<PostLeadListSource["settings"], "post_id" | "type">
  >({ comments: true, likes: true, reposts: true });

  const { linkedInPost, linkedInPostError, isLoadingLinkedInPost } =
    useLinkedInPost(minerAccountId, postUrl);

  const isShowingSelectedPost = postUrl && !linkedInPostError;

  //only the engagement types that are toggled on are targeted
  const leadCount = isShowingSelectedPost
    ? (targetingParameters.likes ? linkedInPost?.likes || 0 : 0) +
      (targetingParameters.comments ? linkedInPost?.comments || 0 : 0) +
      (targetingParameters.reposts ? linkedInPost?.shares || 0 : 0)
    : 0;

  return (
    <ScrollAndFooter
      leadCount={leadCount}
      isCreatingSource={isCreatingSource}
      onCreate={() => {
        if (linkedInPost) {
          createSource({
            miner: minerAccountId,
            settings: {
              type: "post",
              post_id: linkedInPost.urn,
              ...targetingParameters,
            },
          });
        }
      }}
    >
      <div className="flex flex-col gap-y-3 lg:gap-y-6 max-w-135 m-auto">
        <div className="flex gap-3 lg:gap-5 max-lg:order-3 flex-col lg:flex-row">
          <LinkedInAccountFormField
            accountId={minerAccountId}
            setAccountId={setMinerAccountId}
          />
          <PostFormField setPostUrl={setPostUrl} />
        </div>

        {isShowingSelectedPost ? (
          <SelectedPost
            post={linkedInPost}
            isLoading={isLoadingLinkedInPost}
            targetingParameters={targetingParameters}
            setTargetingParameters={setTargetingParameters}
          />
        ) : (
          <>
            <img src={PostImage} alt="" height={238} />
            <ErrorMessage
              className="mt-1.5 max-lg:order-4"
              error={linkedInPostError}
              fallbackErrorMessage="There was an error loading your post, please try again later"
              size="sm"
            />
          </>
        )}
      </div>
    </ScrollAndFooter>
  );
}
